import React, { useState } from "react";

const TimeTable = ({
  currentCombination,
  breaks,
  parseTime,
  formatHour,
  formatTime,
  plannedCourses,
  onCourseClick
}) => {
  const [hoveredGroup, setHoveredGroup] = useState(null);

  // Generate a stable color for each course
  const courseKey = (course) =>
    `${course.dept}-${course.number}`.toLowerCase();

  const colorOptions = [
    "bg-red-400",
    "bg-green-400",
    "bg-blue-400",
    "bg-yellow-300",
    "bg-purple-400",
    "bg-pink-400",
    "bg-indigo-400",
    "bg-teal-400",
    "bg-orange-400",
  ];

  const courseColors = {};
  plannedCourses.forEach((course, index) => {
    const key = courseKey(course);
    courseColors[key] = colorOptions[index % colorOptions.length];
  });

  // Track the earliest and latest class times (in minutes from midnight)
  let minTime = Infinity;
  let maxTime = 0;

  const updateBoundaries = (start, end) => {
    if (start < minTime) minTime = start;
    if (end > maxTime) maxTime = end;
  };

  // We want to group events by day: Mo, Tu, We, Th, Fr
  const days = ["Mo", "Tu", "We", "Th", "Fr"];
  const DAYS_MAP = {
    Mo: "Monday",
    Tu: "Tuesday",
    We: "Wednesday",
    Th: "Thursday",
    Fr: "Friday",
  };

  const eventsByDay = {};
  days.forEach((day) => {
    eventsByDay[day] = [];
  });

  // Populate events from the selected combination
  currentCombination.forEach((courseOption) => {
    if (!courseOption.sfuData || !courseOption.sfuData.courseSchedule) return;

    courseOption.sfuData.courseSchedule.forEach((sched) => {
      // Skip exam entries or anything with missing times
      if (sched.isExam) return;
      if (!sched.startTime || !sched.endTime) return; // TBA or no time

      const start = parseTime(sched.startTime);
      const end = parseTime(sched.endTime);

      // If parseTime() returned 0:00 for TBA or if end <= start, skip
      if ((start === 0 && end === 0) || end <= start) return;

      // Update the overall minTime/maxTime
      updateBoundaries(start, end);

      // e.g. "Mo, We" => ["Mo","We"]
      const dayArr = sched.days ? sched.days.split(",").map((d) => d.trim()) : [];
      const group = `${courseOption.dept.toLowerCase()}-${courseOption.number.toLowerCase()}`;

      dayArr.forEach((day) => {
        if (!eventsByDay[day]) return; // skip if day is outside Mo–Fr
        eventsByDay[day].push({
          type: "course",
          dept: courseOption.dept,
          number: courseOption.number,
          section: courseOption.section || "",
          startTime: sched.startTime,
          endTime: sched.endTime,
          start,
          end,
          color: courseColors[courseKey(courseOption)] || "bg-blue-400",
          group,
          courseOption,
        });
      });
    });
  });

  // Populate break events
  breaks
    .filter((bk) => bk.selected)
    .forEach((bk) => {
      // If break times are invalid, skip
      if (!bk.start || !bk.end) return;
      const start = parseTime(bk.start);
      const end = parseTime(bk.end);
      if ((start === 0 && end === 0) || end <= start) return;

      // Update boundaries
      updateBoundaries(start, end);

      (bk.days || []).forEach((day) => {
        if (!eventsByDay[day]) return;
        eventsByDay[day].push({
          type: "break",
          name: bk.name,
          startTime: bk.start,
          endTime: bk.end,
          start,
          end,
          color: bk.color || "bg-gray-400",
          group: `break-${bk.name.toLowerCase()}`,
        });
      });
    });

  // If we never set minTime or maxTime, default to 8am–5pm
  if (minTime === Infinity) {
    minTime = 8 * 60; // 8:00 AM
    maxTime = 17 * 60; // 5:00 PM
  }

  const pixelsPerMinute = 1;
  const timelineHeight = (maxTime - minTime) * pixelsPerMinute;

  // We'll use a little stripe pattern for breaks
  const stripedBreakStyle = {
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(255,255,255,0.3), rgba(255,255,255,0.3) 10px, transparent 10px, transparent 20px)",
  };

  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div className="min-w-full">
        {/* Header row for days */}
        <div className="grid grid-cols-[4rem_1fr]">
          <div className="border border-gray-300 bg-gray-100"></div>
          <div className="grid grid-cols-5">
            {days.map((day) => (
              <div
                key={day}
                className="border border-gray-300 bg-gray-100 text-center text-sm font-medium p-2"
              >
                {DAYS_MAP[day]}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline rows */}
        <div
          className="relative transition-all duration-500 ease-in-out"
          style={{
            height: timelineHeight + 10 + "px",
            overflow: "visible",
          }}
        >
          {/* Horizontal hour lines */}
          {Array.from(
            { length: Math.ceil((maxTime - minTime) / 60) },
            (_, i) => {
              const top = i * 60 * pixelsPerMinute;
              return (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-t border-gray-300 pointer-events-none"
                  style={{ top: top + "px" }}
                />
              );
            }
          )}

          <div className="grid grid-cols-[4rem_1fr] h-full">
            {/* Time labels on the left */}
            <div className="relative border-r border-gray-300 overflow-visible">
              {Array.from(
                { length: Math.ceil((maxTime - minTime) / 60) },
                (_, i) => {
                  const timeInMinutes = minTime + i * 60;
                  const hour = Math.floor(timeInMinutes / 60);
                  const minutes = timeInMinutes % 60;
                  const timeLabel = `${hour % 12 === 0 ? 12 : hour % 12}:${minutes
                    .toString()
                    .padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
                  const top = i * 60 * pixelsPerMinute;
                  return (
                    <div
                      key={i}
                      className="absolute left-0 w-full text-xs text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                      style={{ top: top + "px" }}
                    >
                      {timeLabel}
                    </div>
                  );
                }
              )}
            </div>

            {/* Days columns */}
            <div className="grid grid-cols-5 relative border-l border-gray-300">
              {days.map((day) => (
                <div
                  key={day}
                  className="relative border-r border-gray-300 overflow-visible"
                  style={{ height: timelineHeight + "px" }}
                >
                  {eventsByDay[day].map((event, index) => {
                    const top = (event.start - minTime) * pixelsPerMinute;
                    const height = (event.end - event.start) * pixelsPerMinute;

                    // For very short events, we can style them a bit differently
                    const MIN_EVENT_HEIGHT = 40;
                    const isSmall = height < MIN_EVENT_HEIGHT;

                    if (event.type === "course") {
                      return (
                        <div
                          key={index}
                          onMouseEnter={() => setHoveredGroup(event.group)}
                          onMouseLeave={() => setHoveredGroup(null)}
                          onClick={() => {
                            if (onCourseClick) {
                              onCourseClick(event.courseOption);
                            }
                          }}
                          className={`absolute left-1 right-1 text-xs p-1 rounded text-white transition-all duration-200 cursor-pointer ${
                            hoveredGroup === event.group
                              ? "ring-2 ring-gray-400"
                              : "hover:shadow-lg"
                          }`}
                          style={{ top: top + "px", height: height + "px" }}
                        >
                          {isSmall ? (
                            <div
                              className={`${event.color} h-full p-1 rounded hover:opacity-90 flex flex-row justify-between items-center`}
                            >
                              <div className="font-semibold">
                                {event.dept.toUpperCase()} {event.number}{" "}
                                {event.section && `(${event.section.toUpperCase()})`}
                              </div>
                              <div>
                                {event.startTime} - {event.endTime}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`${event.color} h-full p-1 rounded hover:opacity-90`}
                            >
                              <div className="font-semibold">
                                {event.dept.toUpperCase()} {event.number}{" "}
                                {event.section && `(${event.section.toUpperCase()})`}
                              </div>
                              <div>
                                {event.startTime} - {event.endTime}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    } else if (event.type === "break") {
                      // Render break blocks with stripes
                      return (
                        <div
                          key={index}
                          onMouseEnter={() => setHoveredGroup(event.group)}
                          onMouseLeave={() => setHoveredGroup(null)}
                          className={`absolute left-1 right-1 text-xs p-1 rounded text-white transition-all duration-200 cursor-pointer ${
                            hoveredGroup === event.group
                              ? "ring-2 ring-gray-400"
                              : "hover:shadow-lg"
                          }`}
                          style={{ top: top + "px", height: height + "px" }}
                        >
                          {isSmall ? (
                            <div
                              className={`${event.color} h-full p-1 rounded hover:opacity-90 flex flex-row justify-between items-center`}
                              style={stripedBreakStyle}
                            >
                              <div className="font-semibold">
                                {event.name} Break
                              </div>
                              <div>
                                {formatTime(event.startTime)} - {formatTime(event.endTime)}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`${event.color} h-full p-1 rounded hover:opacity-90`}
                              style={stripedBreakStyle}
                            >
                              <div className="font-semibold">
                                {event.name} Break
                              </div>
                              <div>
                                {formatTime(event.startTime)} - {formatTime(event.endTime)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeTable;
